import React from "react";
import { ReportMetric, ReportSection, SchoolReport } from "../types";
import kvsLogo from "../../../assets/kvsLogo.svg";
import "./ReportDocument.css";

/**
 * Vidyalaya Assessment Report, rendered as an official document.
 *
 * Clauses are numbered because an official report is cited by clause: an
 * observation at 2.3 names the clause it was drawn from, so a reader can check
 * it rather than take it on trust. That cross-reference is the reason for the
 * numbering, not decoration.
 *
 * Findings are comparative. A figure in the bottom quarter of all active KVs is
 * recorded as requiring attention, the top quarter as satisfactory, and the
 * middle half is not remarked on. No threshold is invented anywhere.
 */

/** Where the school sits on the distribution, phrased from its position. */
function comparison(metric: ReportMetric): string {
  if (metric.percentile === null || metric.national_median === null) {
    return metric.note || "no comparison available";
  }
  // Phrased so the SUBJECT is the other schools, not this one.
  //
  // "Lower than 5% of KVs" was read as a small number when it meant this school
  // is beaten by 95% of them — and on an inverted metric like rooms out of use
  // the reader could not tell whether "lower" was good news. Saying how many
  // KVs sit below this figure removes both problems: the sentence states a
  // position and nothing else, and the verdict colour says whether it is good.
  const below = Math.round(metric.percentile);
  if (below >= 90) return `${below}% of KVs are below this`;
  if (below <= 10) return `Only ${below}% of KVs are below this`;
  return `${below}% of KVs are below this`;
}

function ordinal(percentile: number): string {
  const n = Math.round(percentile);
  const suffix = n % 10 === 1 && n % 100 !== 11 ? "st"
    : n % 10 === 2 && n % 100 !== 12 ? "nd"
    : n % 10 === 3 && n % 100 !== 13 ? "rd" : "th";
  return `${n}${suffix} percentile`;
}

function formatValue(metric: ReportMetric): string {
  // Compliance particulars carry no number at all.
  if (metric.text_value) return metric.text_value;
  if (metric.value === null) return "—";
  const shown = Number.isInteger(metric.value)
    ? metric.value.toLocaleString()
    : metric.value.toFixed(1);
  return `${shown}${metric.unit}`;
}

/** Position on the national distribution, drawn as a printed scale. */
const Scale: React.FC<{ metric: ReportMetric }> = ({ metric }) => {
  // Compliance findings have nothing to rank against; their note IS the
  // position ("Expired 240 days before this report").
  if (metric.percentile === null) {
    return <span className="vr-note">{metric.note || "Not ranked"}</span>;
  }
  return (
    <>
      <div className="vr-scale">
        <span className="vr-caret" style={{ left: `${metric.percentile}%` }} />
      </div>
      <span className="vr-note">
        {metric.verdict === "neutral" ? ordinal(metric.percentile) : comparison(metric)}
      </span>
    </>
  );
};

const MetricTable: React.FC<{ section: ReportSection; clause: number }> = ({ section, clause }) => {
  // Safety is entirely compliance findings — a certificate is held or it is
  // not, and there is no national median for "held". The column was printing a
  // full stack of dashes and inviting the reader to look for a comparison that
  // does not exist. Driven off the data rather than a section name, so any
  // future unranked section behaves the same way.
  const ranked = section.metrics.some(m => m.national_median !== null);
  return (
  <div className="vr-table-wrap">
    <table>
      <thead>
        <tr>
          <th className="vr-sl">Sl.</th>
          <th>Particular</th>
          <th className="vr-num">Value</th>
          {ranked && <th className="vr-num">All-India median</th>}
          <th>{ranked ? "Position among all KVs" : "Status"}</th>
        </tr>
      </thead>
      <tbody>
        {section.metrics.map((metric, i) => (
          <tr key={metric.label} data-sev={metric.verdict}>
            <td className="vr-sl">{clause}.{i + 1}</td>
            <td>
              {metric.label}
              {metric.note && <span className="vr-note">{metric.note}</span>}
            </td>
            <td className="vr-num">{formatValue(metric)}</td>
            {ranked && (
              <td className="vr-num">
                {metric.national_median === null ? "—" : metric.national_median.toLocaleString()}
              </td>
            )}
            <td><Scale metric={metric} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

/** Array-valued facts (rooms by type, enrolment by class) as ruled tables. */
const FactTable: React.FC<{ title: string; rows: Record<string, unknown>[] }> = ({ title, rows }) => {
  if (rows.length === 0) return null;
  const columns = Object.keys(rows[0]);
  return (
    <div className="vr-table-wrap">
      <table>
        <caption>{title}</caption>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c} className={typeof rows[0][c] === "number" ? "vr-num" : undefined}>
                {c.replace(/_/g, " ")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map(c => (
                <td key={c} className={typeof row[c] === "number" ? "vr-num" : undefined}>
                  {row[c] === null || row[c] === undefined ? "—" : String(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Clause: React.FC<{ no: number; title: string; children: React.ReactNode }> =
  ({ no, title, children }) => (
    <section className="vr-clause">
      <h2><span className="vr-no">{no}.</span> {title}</h2>
      {children}
    </section>
  );

const ReportDocument: React.FC<{ report: SchoolReport }> = ({ report }) => {
  const profile = report.sections.find(s => s.key === "profile");
  const rest = report.sections.filter(s => s.key !== "profile");

  // Clause 1 particulars, 2 attention, 3 satisfactory, then one per section.
  const firstSectionClause = 4;

  // The clause-reference map that lived here is gone with the Clause columns.
  // Observations still carry their serial number within clauses 2 and 3, and
  // the numbering inside each section clause is unchanged, so a figure remains
  // findable — it is simply not cross-referenced from the summary tables.
  const drawn = new Date();
  const stamp = drawn.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });

  const scalarFacts = (section: ReportSection) =>
    Object.entries(section.facts).filter(([, v]) => v !== null && v !== undefined && !Array.isArray(v));
  const tableFacts = (section: ReportSection) =>
    Object.entries(section.facts).filter(([, v]) => Array.isArray(v) && (v as unknown[]).length > 0);

  const basisClause = firstSectionClause + rest.length;

  return (
    <article className="vidyalaya-report">

      <header>
        <div className="vr-authority">
          <img className="vr-emblem" src={kvsLogo}
               alt="Kendriya Vidyalaya Sangathan" />
          <div className="vr-authority-lines">
            <span className="vr-gov">Government of India</span>
            <span className="vr-ministry">Ministry of Education · Department of School Education &amp; Literacy</span>
            <span className="vr-bodyname">Kendriya Vidyalaya Sangathan</span>
          </div>
        </div>

        <div className="vr-tricolour" aria-hidden="true"><i /><i /><i /></div>

        <div className="vr-dochead">
          <div>
            <span className="vr-kicker">Vidyalaya Assessment Report</span>
            <h1>{report.school_name}</h1>
          </div>
          <div className="vr-docket">
            KV Code <b>{report.school_id}</b><br />
            Region <b>{report.region}</b><br />
            Drawn <b>{stamp}</b>
          </div>
        </div>

        <p className="vr-classification">
          For official use — figures as held at the moment of drawing
        </p>
      </header>

      {profile && (
        <Clause no={1} title="Particulars of the Vidyalaya">
          <dl className="vr-particulars">
            {scalarFacts(profile).map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{String(v)}</dd></div>
            ))}
          </dl>
        </Clause>
      )}

      <Clause no={2} title="Observations requiring attention">
        {report.needs_attention.length === 0 ? (
          <div className="vr-nil">
            <strong>Nil</strong>
            <p>No figure in this report falls in the bottom quarter of all
              Kendriya Vidyalayas.</p>
          </div>
        ) : (
          <div className="vr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="vr-sl">Sl.</th>
                  <th>Observation</th>
                  <th>Grade</th>
                  <th className="vr-num">Recorded</th>
                  <th className="vr-num">Median</th>
                </tr>
              </thead>
              <tbody>
                {report.needs_attention.map((metric, i) => (
                  <tr key={metric.label} data-sev={metric.verdict}>
                    <td className="vr-sl">2.{i + 1}</td>
                    <td className="vr-finding">
                      {metric.label}
                      {metric.percentile === null
                        ? <span className="vr-note">{metric.note}</span>
                        : ` — ${comparison(metric).toLowerCase()}`}
                    </td>
                    <td>
                      <span className={metric.verdict === "critical"
                        ? "vr-sev vr-sev-critical" : "vr-sev vr-sev-attention"}>
                        {metric.verdict === "critical" ? "Critical" : "Attention"}
                      </span>
                    </td>
                    <td className="vr-num">{formatValue(metric)}</td>
                    <td className="vr-num">
                      {metric.national_median === null ? "—" : metric.national_median.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Clause>

      <Clause no={3} title="Recorded as satisfactory">
        {report.strengths.length === 0 ? (
          <div className="vr-nil">
            <strong>Nil</strong>
            <p>No figure in this report falls in the top quarter of all
              Kendriya Vidyalayas.</p>
          </div>
        ) : (
          <div className="vr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="vr-sl">Sl.</th>
                  <th>Observation</th>
                  <th className="vr-num">Recorded</th>
                  <th className="vr-num">Median</th>
                </tr>
              </thead>
              <tbody>
                {report.strengths.map((metric, i) => (
                  <tr key={metric.label} data-sev="strength">
                    <td className="vr-sl">3.{i + 1}</td>
                    <td className="vr-finding">
                      {metric.label}
                      {metric.note && <span className="vr-note">{metric.note}</span>}
                    </td>
                    <td className="vr-num">{formatValue(metric)}</td>
                    <td className="vr-num">
                      {metric.national_median === null ? "—" : metric.national_median.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Clause>

      {rest.map((section, si) => (
        <Clause key={section.key} no={firstSectionClause + si} title={section.title}>
          {/* An unreachable section is stated, never dropped: an absent clause
              would read as a Vidyalaya with no observations in that area. */}
          {section.unavailable ? (
            <div className="vr-nil">
              <strong>Not available for this report</strong>
              <p>{section.unavailable}</p>
            </div>
          ) : (
            <>
              {scalarFacts(section).length > 0 && (
                <dl className="vr-particulars">
                  {scalarFacts(section).map(([k, v]) => (
                    <div key={k}><dt>{k}</dt><dd>{String(v)}</dd></div>
                  ))}
                </dl>
              )}
              {section.metrics.length > 0 && (
                <MetricTable section={section} clause={firstSectionClause + si} />
              )}
              {tableFacts(section).map(([k, v]) => (
                <FactTable key={k} title={k} rows={v as Record<string, unknown>[]} />
              ))}
            </>
          )}
        </Clause>
      ))}

      <Clause no={basisClause} title="Basis of assessment">
        <p className="vr-lede">
          Observations at clauses 2 and 3 are derived from figures recorded
          elsewhere in this report and cite the clause they come from. Nothing is
          graded against a target: a figure in the bottom quarter of all active
          Kendriya Vidyalayas is recorded as requiring attention, the top quarter
          as satisfactory, and the middle half is not remarked upon.
        </p>
        <p className="vr-lede">
          The all-India median is printed beside every figure so each observation
          can be checked independently. Where a smaller value is the better
          outcome, the particular says so. Figures carried for context are
          reported without any judgement being implied.
        </p>

        <div className="vr-attest">
          <div className="vr-sig"><span className="vr-sig-line" /><span className="vr-sig-role">Prepared by</span></div>
          <div className="vr-sig"><span className="vr-sig-line" /><span className="vr-sig-role">Principal</span></div>
          <div className="vr-sig"><span className="vr-sig-line" /><span className="vr-sig-role">Deputy Commissioner, {report.region} Region</span></div>
        </div>
      </Clause>

      <footer className="vr-footer">
        <span>Samiksha Samadhan · Vidyalaya Assessment Report · KV {report.school_id}</span>
        <span>Drawn {stamp}</span>
      </footer>

    </article>
  );
};

export default ReportDocument;
